<template>
  <Modal
    :title="editModalTitle"
    bottom-close-button-label="保存"
    :isCloseButtonDisabled="isSaveButtonDisabled"
    @close="save"
    @dismiss="$emit('close')"
  >
    <template v-slot:body>
      <div>
        <div class="form-group">
          <label class="form-control-label">事業所</label>
          <select class="form-control" v-model="state.device.group2Id" @change="state.device.groupId = null">
            <option v-for="e in availableGroups2" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label">部署</label>
          <select class="form-control" v-model="state.device.groupId" :disabled="!selectedGroup2">
            <option v-for="e in filteredGroups3" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label">
            車載器ID
          </label>
          <input type="text" class="form-control" v-model="state.device.deviceId" :readonly="isDeviceIdInputReadonly"/>
        </div>
        <div class="form-group">
          <label class="form-control-label">
            車両番号
          </label>
          <input type="text" class="form-control" v-model="state.device.carName" />
        </div>
        <div class="form-group">
          <label class="form-control-label">車種</label>
          <select class="form-control" v-model="state.device.carKind">
            <option v-for="e in carKinds" :key="e.key" :value="e.key">
              {{e.val}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label">
            利用停止日
          </label>
          <MyCalendar
            class="stopped-use-on-date"
            :date-value="state.device.stoppedUseOn"
            @date-input="state.device.stoppedUseOn = $event"
          />
        </div>
        <div class="form-group">
          <label class="form-control-label">備考</label>
          <textarea class="form-control" maxlength="250" v-model="state.device.bikou1" />
        </div>
        <div class="form-group errors">
          <div class="error" v-for="(e, i) in httpErrorMsgs" :key="`h-${i}`">
            {{e}}
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useEditModalCommon } from 'src/composables/useEditModalCommon';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { Lov } from 'src/models/master';
import type { AdminDeviceParams } from 'src/models/apis/device/adminDeviceRequest';
import type { DeviceInputModel } from 'src/models/device';
import { createDeviceInitInputModel } from 'src/models/device';
import { dtFormat } from 'src/lib/dateHelper';

const props = defineProps<{
  isEditMode: boolean;
  device: DeviceInputModel;
  availableGroups2: AdminGroup2[];
  availableGroups3: AdminGroup3[];
  carKinds: Lov[];
  isRequesting: boolean;
  httpErrorMsgs: string[];
}>();

const emit = defineEmits<{
  (e: 'create', createData: AdminDeviceParams): void;
  (e: 'update', deviceId: string, updateData: AdminDeviceParams): void;
  (e: 'close'): void;
}>();

const state = reactive<{ device: DeviceInputModel }>({
  device: createDeviceInitInputModel(),
});

const { editModalTitle } = useEditModalCommon(props, () => props.device, (copied) => {
  state.device = copied;
});

const isDeviceIdInputReadonly = computed<boolean>(() => props.isEditMode);

const selectedGroup2 = computed<boolean>(() => {
  return state.device.group2Id != null;
});

const filteredGroups3 = computed<AdminGroup3[]>(() => {
  return props.availableGroups3.filter(group3 => group3.parentId === state.device.group2Id);
});

const validateDeviceForm = (device: DeviceInputModel): boolean => {
  if (!Number.isInteger(device.group2Id)) return false;
  if (!Number.isInteger(device.groupId)) return false;
  if (device.deviceId.length <= 0) return false;
  if (device.deviceId.length > 32) return false;
  if (device.carName.length <= 0) return false;
  if (!device.carKind) return false;
  if (device.bikou1 && device.bikou1.length > 250) return false;

  return true;
};

const isSaveButtonDisabled = computed<boolean>(() => {
  return props.isRequesting || !validateDeviceForm(state.device);
});

const toPayload = (): AdminDeviceParams => {
  return {
    device_id: state.device.deviceId,
    car_name: state.device.carName,
    car_kind: state.device.carKind ?? '',
    group2_id: state.device.group2Id ?? 0,
    group_id: state.device.groupId ?? 0,
    stopped_use_on: dtFormat(state.device.stoppedUseOn, 'yyyy/mm/dd'),
    bikou1: state.device.bikou1,
  };
};

const createDevice = (): void => {
  if (isSaveButtonDisabled.value) return;

  emit('create', toPayload());
};

const updateDevice = (): void => {
  if (isSaveButtonDisabled.value) return;

  emit('update', state.device.deviceId, toPayload());
};

const save = (): void => {
  if (props.isEditMode) {
    updateDevice();
  } else {
    createDevice();
  }
};
</script>

<style lang="scss" scoped>
.stopped-use-on-date {
  width: 100%;
}
</style>
